import React, { Fragment, useState, useEffect } from 'react'
import { Button } from '@material-tailwind/react';
import { Dialog, Transition } from '@headlessui/react';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import UploadDetail from './UploadDetail';

export default function MoreDetailsModal({ upload, getUploaded, open, setOpen }) {
  const [maxTitleLength, setMaxTitleLength] = useState(100);
  const [maxDescriptionLength, setMaxDescriptionLength] = useState(100);
  const [uid, setUid] = useState(upload.uploadId);
  const navigate = useNavigate();
  const dateFormat = "dd MMMM yyyy',' hh:mm:ss a";

  useEffect(() => {
    getMaxLengths();
  }, [])

  useEffect(() => {
    setUid(upload.uploadId);
  }, [upload.uploadId])

  async function getMaxLengths() {
    const response = await fetch("https://uploads.jellyo.net/api/upload/maxdetaillength", { credentials: "include"});
    const responseData = await response.json();
    setMaxTitleLength(responseData.maxTitleLength);
    setMaxDescriptionLength(responseData.maxDescriptionLength)
  }

  function validateUploadId(uploadId) {
    if (uploadId.length < 4) {
      return { status: "error", message: "Minimum length of custom URL is 4" };
    }

    if (uploadId.length > 15) {
      return { status: "error", message: "Maximum length of custom URL is 15" };
    }

    if (/[^A-Za-z0-9]/.test(uploadId)) {
      return { status: "error", message: "Custom URL must be alphanumeric" };
    }

    return true;
  }

  function validateUploadTitle(title) {
    if (title.length > maxTitleLength) {
      return { status: "error", message: "Maximum length of title is 100" };
    }

    return true;
  }

  function validateUploadDescription(description) {
    if (description.length > maxDescriptionLength) {
      return { status: "error", message: "Maximum length of description is 500" };
    }
    return true;
  }

  function handleInputChange(uploadEntry, update) {
    const validationMapping = {
      uploadId: validateUploadId,
      uploadTitle: validateUploadTitle,
      uploadDescription: validateUploadDescription
    };

    const validate = validationMapping[uploadEntry];

    if (validate) {
      const { message } = validate(update);
      if (message) return message;
    } else {
      console.error(`No validation function available for ${uploadEntry}`);
    }
    return true;
  }

  async function handleSave(uploadEntry, update) {
    const response = await fetch("https://uploads.jellyo.net/api/upload/update", {
      credentials: "include",
      method: "POST",
      headers: {
        "content-type": "application/json"
      },
      body: JSON.stringify({
        uploadId: uid,
        uploadEntry,
        update,
      })
    });

    const responseData = await response.json();
    if (responseData.status === "success" && uploadEntry === "uploadId") {
      setUid(update);
    }
    return responseData;
  }

  function handleClose() {
    setOpen(!open);
    getUploaded();
  }

  // TODO: Add file size in more details
  return (
    <Transition appear show={open} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/50" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white text-left align-middle shadow-xl transition-all">
                <Dialog.Title
                  as="h3"
                  className="text-2xl py-5 px-6 font-semibold leading-6 text-gray-900 flex justify-between items-center"
                >
                  <span>Upload Details</span>
                  <i onClick={handleClose} className='bx bx-x cursor-pointer'></i>
                </Dialog.Title>
                  <hr />
                <div className='flex flex-col text-lg pt-4 p-6 font-light'>
                  <span>Upload Type: {upload.type && (upload.type[0].toUpperCase() + upload.type.substring(1))}</span>
                  <UploadDetail cb={handleSave} handleInputChange={handleInputChange} type="input" label="Upload ID" uploadEntry="uploadId" uploadEntryValue={upload.uploadId} />
                  <UploadDetail cb={handleSave} handleInputChange={handleInputChange} type="input" label="Title" maxLength={maxTitleLength} uploadEntry="uploadTitle" uploadEntryValue={upload.title} />
                  <UploadDetail cb={handleSave} handleInputChange={handleInputChange} type="textarea" label="Description" maxLength={maxDescriptionLength} uploadEntry="uploadDescription" uploadEntryValue={upload.description} />
                  <span>Created: {upload.createdAt && format(new Date(upload.createdAt), dateFormat)}</span>
                  <span>Expiring: {upload.expiresAt && format(new Date(upload.expiresAt), dateFormat)}</span>
                  <Button onClick={() => navigate(`/${uid}`)} className='mt-4'>Go to upload</Button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}
